import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  useRouterState,
} from "@tanstack/react-router";
import {
  Activity,
  BarChart3,
  ChevronsLeft,
  ChevronsRight,
  Gauge,
  LayoutDashboard,
  Printer,
  Table,
} from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { SerialFilterDialog } from "@/components/serials/SerialFilterDialog";
import { ROUTES } from "@/shared/constants";
import { i18n, useTranslation, type AppLocaleTag, type AppMessageKey } from "@/shared/i18n";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectLocale } from "@/shared/i18n";
import { setLocale } from "@/store/localeSlice";
import { cn } from "@/shared/utils/cn";

/**
 * TanStack Router route tree (D8):
 * - code-based routes in this file, each mapping to a lazy `src/pages/` component;
 * - page-level filters live in validated search params (shareable URLs);
 * - all in-app navigation uses typed `Link`/`useNavigate`.
 */

/** Supported `lang` search-param values (validated — bad params fall back). */
const LANG_PARAM_VALUES = ["en-US", "ja-JP", "fr-FR"] as const;

/**
 * Page-level filter schema shared by the Basic Information pages
 * (section 32: global date range + selected machine).
 * Pages read/write these via the router — never Redux (NFR10).
 */
const basicInformationSearchSchemaBase = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  machine: z.string().optional(),
});

/**
 * Full search schema: page filters + `lang`, a page-level context param.
 * The URL is the source of truth for the UI language (default `en-US` when
 * absent), synced to app Redux (number formatting) and i18next
 * (translations) by the AppShell.
 */
export const basicInformationSearchSchema = basicInformationSearchSchemaBase.extend({
  lang: z.enum(LANG_PARAM_VALUES).optional(),
});

export type BasicInformationSearch = z.infer<typeof basicInformationSearchSchema>;

/**
 * App shell, responsive (requirement 1):
 * - laptop/desktop (md+): fixed left toolbar on a prominent dark rail —
 *   pinned to the viewport (never scrolls with content), collapsible via a
 *   shadcn ghost Button on the brand row (logo + site title), with shadcn
 *   tooltips labeling the icons while collapsed;
 * - mobile (<md): brand-only top bar + fixed bottom navigation bar.
 */
const NAV_ITEMS = [
  { to: ROUTES.dashboard, labelKey: "nav.dashboard" as AppMessageKey, icon: LayoutDashboard },
  { to: ROUTES.basicInformation.hd, labelKey: "nav.basicInfoHd" as AppMessageKey, icon: Table },
  { to: ROUTES.oee, labelKey: "nav.oee" as AppMessageKey, icon: Gauge },
  { to: ROUTES.paperUsage, labelKey: "nav.paperUsage" as AppMessageKey, icon: Printer },
  { to: ROUTES.printOverview, labelKey: "nav.printOverview" as AppMessageKey, icon: BarChart3 },
  { to: ROUTES.runningStatus, labelKey: "nav.runningStatus" as AppMessageKey, icon: Activity },
] as const;

function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const locale = useAppSelector(selectLocale);

  // `?lang=` is the source of truth for the UI language: sync the validated
  // URL param into app Redux (number formatting) + i18next (translations).
  // Absent param keeps the current/default language.
  const lang = useRouterState({
    select: (state) =>
      (state.location.search as { lang?: AppLocaleTag }).lang,
  });
  useEffect(() => {
    if (lang && lang !== locale) {
      dispatch(setLocale(lang));
      void i18n.changeLanguage(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Navigation keeps the current `lang` param (links merge it into each
  // route's search params so the language survives page switches).
  const keepLang = (prev: Record<string, unknown>) => ({ ...prev, lang });

  return (
    // overflow-x-hidden: the page never scrolls horizontally (the data table
    // keeps its own internal horizontal scrollbar).
    <div className="flex min-h-screen overflow-x-hidden bg-background text-foreground">
      {/* Laptop: fixed left toolbar — pinned to the viewport, it never
          scrolls away with the page content. Links are plain (no wrapper
          that can block clicks); when collapsed, the native `title`
          attribute labels the icons. */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-20 hidden flex-col border-r border-zinc-800 bg-zinc-900 text-zinc-50 transition-[width] duration-200 md:flex",
          collapsed ? "w-14" : "w-60",
        )}
      >
        {/* Brand row: logo + site title, with the collapse toggle on the
            same row (right end). Collapsed: the toggle alone, centered. */}
        <div
          className={cn(
            "flex h-14 flex-none items-center gap-2 border-b border-zinc-800 px-2",
            collapsed && "justify-center",
          )}
        >
          {!collapsed && (
            <>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-500 font-bold text-zinc-950">
                T
              </span>
              <span className="truncate font-semibold">TDD Dashboard</span>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand toolbar" : "Collapse toolbar"}
            className={cn(
              "size-7 shrink-0 text-zinc-400 hover:bg-zinc-800 hover:text-white",
              collapsed ? "mx-auto" : "ml-auto",
            )}
          >
            {collapsed ? (
              <ChevronsRight className="size-4" />
            ) : (
              <ChevronsLeft className="size-4" />
            )}
          </Button>
        </div>

        <nav className="flex flex-col gap-1 p-2">
          {NAV_ITEMS.map(({ to, labelKey, icon: Icon }) => {
            const label = t(labelKey);
            return (
              <Link
                key={to}
                to={to}
                search={keepLang}
                title={collapsed ? label : undefined}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-white",
                  collapsed && "justify-center px-0",
                )}
                activeProps={{ className: "bg-zinc-800 font-medium text-white" }}
              >
                <Icon className="size-4 shrink-0" />
                {!collapsed && <span className="truncate">{label}</span>}
              </Link>
            );
          })}
        </nav>
        {/* App-wide serial filter: bottom of the rail (it is a global
            control, not a navigation item). */}
        <div className={cn("mt-auto p-2", collapsed && "px-0")}>
          <SerialFilterDialog variant="sidebar" />
        </div>
      </aside>

      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col transition-[padding] duration-200",
          collapsed ? "md:pl-14" : "md:pl-60",
        )}
      >
        {/* Mobile top bar: brand only — navigation lives in the bottom bar. */}
        <header className="flex h-12 items-center gap-2 border-b border-border px-4 md:hidden">
          <span className="flex size-6 items-center justify-center rounded-md bg-emerald-500 font-bold text-zinc-950">
            T
          </span>
          <span className="font-semibold">TDD Dashboard</span>
          <SerialFilterDialog variant="icon" />
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 p-4 pb-24 md:p-6 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile: fixed bottom navigation bar, same prominent dark rail. */}
      <nav className="fixed inset-x-0 bottom-0 z-20 flex border-t border-zinc-800 bg-zinc-900 text-zinc-50 md:hidden">
        {NAV_ITEMS.map(({ to, labelKey, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            search={keepLang}
            className="flex flex-1 flex-col items-center gap-0.5 py-2 text-xs text-zinc-300 transition-colors hover:text-white"
            activeProps={{ className: "bg-zinc-800 font-medium text-white" }}
          >
            <Icon className="size-4" />
            {t(labelKey)}
          </Link>
        ))}
      </nav>
    </div>
  );
}

const rootRoute = createRootRoute({ component: AppShell });

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.dashboard,
  validateSearch: basicInformationSearchSchema,
  component: lazyRouteComponent(() => import("./pages/Dashboard"), "Dashboard"),
});

const basicInformationHdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.basicInformation.hd,
  validateSearch: basicInformationSearchSchema,
  component: lazyRouteComponent(
    () => import("./pages/BasicInformationHD"),
    "BasicInformationHD",
  ),
});

const oeeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.oee,
  component: lazyRouteComponent(() => import("./pages/Oee"), "Oee"),
});

const paperUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.paperUsage,
  component: lazyRouteComponent(() => import("./pages/PaperUsage"), "PaperUsage"),
});

const printOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.printOverview,
  component: lazyRouteComponent(
    () => import("./pages/PrintOverview"),
    "PrintOverview",
  ),
});

const runningStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTES.runningStatus,
  component: lazyRouteComponent(
    () => import("./pages/RunningStatus"),
    "RunningStatus",
  ),
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  basicInformationHdRoute,
  oeeRoute,
  paperUsageRoute,
  printOverviewRoute,
  runningStatusRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
